// Shared client/server protocol. Imported by both the Worker and web/.

/**
 * Path the app is mounted on: it is served as part of the WorkBrawl hub at
 * https://workbrawl.com/memobrawl/. Static files live in public/memobrawl/ to match.
 */
export const BASE_PATH = "/memobrawl";

/** People per room. PLAYER_COLORS must have at least this many colors. */
export const MAX_PLAYERS = 12;

/** Round lengths the room creator can pick, in seconds. */
export const DURATIONS = [120, 180, 300] as const;
export type Duration = (typeof DURATIONS)[number];
export const DEFAULT_DURATION: Duration = 180;

export const COUNTDOWN_MS = 3000;
/** A wrong pair stays face up this long, so everyone can memorize it, before the turn passes. */
export const MISMATCH_MS = 1500;
/** How long the last pair stays on screen before the results. */
export const REVEAL_MS = 1500;
/**
 * Time for a turn: to flip the first card, and again for each card after it. Running out
 * passes the turn, so one distracted person doesn't hold up the room.
 */
export const TURN_MS = 10_000;

/** Board sizes, smallest first. */
export const SIZES = {
  small: { cols: 4, rows: 4 },
  medium: { cols: 6, rows: 4 },
  large: { cols: 6, rows: 6 },
  xlarge: { cols: 8, rows: 6 },
} as const;
export type Size = keyof typeof SIZES;
export const DEFAULT_SIZE: Size = "medium";
/** Sizes offered when creating a room; the largest is only reached automatically (see boardSizeFor). */
export const SELECTABLE_SIZES = ["small", "medium", "large"] as const satisfies readonly Size[];
const SIZE_ORDER = Object.keys(SIZES) as Size[];

/**
 * The board actually dealt: the size picked for the room, grown with the people in it so
 * there are enough pairs to go around (4+ people: medium, 7+: large, 10+: extra large).
 */
export function boardSizeFor(chosen: Size, people: number): Size {
  const needed: Size = people >= 10 ? "xlarge" : people >= 7 ? "large" : people >= 4 ? "medium" : "small";
  return SIZE_ORDER.indexOf(needed) > SIZE_ORDER.indexOf(chosen) ? needed : chosen;
}

/** Rounds per series, picked when the room is created. After the last one: final standings. */
export const ROUND_COUNTS = [1, 3, 5] as const;
export type RoundCount = (typeof ROUND_COUNTS)[number];
export const DEFAULT_ROUND_COUNT: RoundCount = 1;

export function isRoundCount(value: unknown): value is RoundCount {
  return (ROUND_COUNTS as readonly unknown[]).includes(value);
}

/** One person's line in the series standings. */
export interface SeriesStanding {
  id: string;
  name: string;
  slot: number;
  /** Points in each finished round of the series, in order (0 for rounds they missed). */
  scores: number[];
  total: number;
}

/** Where the room is in its series of rounds. */
export interface SeriesInfo {
  length: RoundCount;
  /** 1-based number of the current (or just finished) round within the series. */
  index: number;
  /** Totals over the finished rounds of the series, best first. */
  standings: SeriesStanding[];
}

/** Points for a pair, and the extra for each pair found right after another one. */
export const PAIR_POINTS = 1;
export const STREAK_BONUS = 1;

/** What can be on a card. Pictures, so the cards read the same in every language. */
export const SYMBOLS = [
  "\u{1F34E}", "\u{1F34C}", "\u{1F347}", "\u{1F349}", "\u{1F353}", "\u{1F352}",
  "\u{1F34D}", "\u{1F95D}", "\u{1F951}", "\u{1F436}", "\u{1F431}", "\u{1F98A}",
  "\u{1F43C}", "\u{1F438}", "\u{1F419}", "\u{1F98B}", "\u{1F33B}", "⭐",
  "\u{1F680}", "\u{1F388}", "\u{1F3B8}", "☕", "\u{1F308}", "\u{1F335}",
] as const;

/**
 * lobby: waiting for someone to start. countdown: 3-2-1 before a round.
 * running: everyone flips cards on the same board while the clock counts down.
 * finished: the ranking is on screen.
 */
export type RoomStatus = "lobby" | "countdown" | "running" | "finished";

// Mid-tone colors that keep contrast on both light and dark backgrounds. Indexed by slot.
export const PLAYER_COLORS = [
  "#e5484d", // red
  "#0b84e5", // blue
  "#2f9e44", // green
  "#f08c00", // orange
  "#8e4ec6", // purple
  "#0f9d9a", // teal
  "#d6409f", // pink
  "#a0703c", // brown
  "#c79a00", // gold
  "#5b5bd6", // indigo
  "#7aa116", // lime
  "#6b7785", // slate
];

export interface PlayerInfo {
  id: string;
  name: string;
  /** 0..MAX_PLAYERS-1: picks the color that marks each person's cards. */
  slot: number;
  score: number;
  online: boolean;
}

export interface RankingEntry {
  id: string;
  name: string;
  slot: number;
  score: number;
}

export type ClientMsg = { type: "flip"; cell: number } | { type: "start" } | { type: "ping" };

/** JSON body of POST /api/rooms. */
export interface CreateRoomRequest {
  duration?: Duration; // omitted = DEFAULT_DURATION
  size?: Size; // omitted = DEFAULT_SIZE; only SELECTABLE_SIZES
  rounds?: RoundCount; // omitted = DEFAULT_ROUND_COUNT
  turnstileToken: string;
}

/** JSON body of POST /api/rooms/:code/verify (join by code from the home screen). */
export interface VerifyRoomRequest {
  turnstileToken: string;
}

/** Response of GET /api/config. */
export interface ClientConfig {
  turnstileSiteKey: string;
}

/** Error codes returned as `{ error }` by the HTTP API. */
export type ApiErrorCode = "INVALID_REQUEST" | "TURNSTILE_FAILED" | "ROOM_NOT_FOUND";

/** The board on screen, as every client sees it. Cells are col + row * cols. */
export interface BoardView {
  cols: number;
  rows: number;
  /**
   * open: the person whose turn it is can flip. miss: a wrong pair is on display before the
   * turn passes. cleared: every pair is found; the results follow.
   */
  phase: "open" | "miss" | "cleared";
  /** Slot of the person whose turn it is (-1 when nobody can flip). */
  turn: number;
  /** Milliseconds left in the current turn. */
  turnMs: number;
  /** Cards face up right now (being tried): [cell, symbol index, slot of who flipped it]. */
  up: [number, number, number][];
  /** Pairs already found: [cell, symbol index, slot of who found it]. */
  taken: [number, number, number][];
  /** The pair just found, for the animation: [cell, cell]. */
  last: [number, number] | null;
}

export type ServerMsg =
  | {
      type: "state";
      you: string;
      status: RoomStatus;
      /** Round length in seconds. */
      duration: Duration;
      size: Size;
      /** Milliseconds left in the countdown (status "countdown") or the round ("running"). */
      remainingMs: number;
      players: PlayerInfo[];
      /** Last finished round, best first. Empty before the first one ends. */
      ranking: RankingEntry[];
      series: SeriesInfo;
    }
  | ({
      /** Sent whenever something about the board changes. */
      type: "board";
      /** Milliseconds left in the round. */
      t: number;
      /** [slot, points] for every player. */
      p: [number, number][];
    } & BoardView)
  | { type: "player_joined"; player: PlayerInfo }
  | { type: "player_updated"; player: PlayerInfo }
  | { type: "player_left"; id: string }
  | { type: "error"; code: ErrorCode; message: string }
  | { type: "pong" };

/**
 * Exact keep-alive frames. The room registers them as a WebSocket auto-response, so
 * Cloudflare answers pings at the edge WITHOUT waking the Durable Object: an idle room
 * with open tabs stays hibernated (no billable duration). The client must send PING_FRAME
 * byte for byte, or the ping falls through to the room and wakes it.
 */
export const PING_FRAME = '{"type":"ping"}';
export const PONG_FRAME = '{"type":"pong"}';

export type ErrorCode = "ROOM_FULL" | "ROOM_NOT_FOUND" | "BAD_MESSAGE";

export const ROOM_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const ROOM_CODE_LENGTH = 5;
export const NAME_MAX_LENGTH = 20;

const ROOM_CODE_RE = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidRoomCode(code: string): boolean {
  return ROOM_CODE_RE.test(code);
}

export function isValidPlayerId(id: string): boolean {
  return UUID_RE.test(id);
}

export function isDuration(value: unknown): value is Duration {
  return (DURATIONS as readonly unknown[]).includes(value);
}

export function isSize(value: unknown): value is Size {
  return typeof value === "string" && Object.hasOwn(SIZES, value);
}

/** A size that can be picked when creating a room. */
export function isSelectableSize(value: unknown): value is (typeof SELECTABLE_SIZES)[number] {
  return (SELECTABLE_SIZES as readonly unknown[]).includes(value);
}

/**
 * Trims and collapses whitespace. Returns null when the name is empty, longer than
 * NAME_MAX_LENGTH characters, or contains HTML-ish or control characters.
 */
export function sanitizeName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, " ");
  const length = [...name].length;
  if (length < 1 || length > NAME_MAX_LENGTH) return null;
  if (/[<>&"'`\p{Cc}]/u.test(name)) return null;
  return name;
}

/** Best score first; ties keep the lower slot (who joined earlier) ahead. */
export function rank<T extends { score: number; slot: number }>(players: readonly T[]): T[] {
  return [...players].sort((a, b) => b.score - a.score || a.slot - b.slot);
}
